/**
 * Combat system: every armed unit and firing building picks one target in
 * range, damage is tallied for everyone and then applied (simultaneous
 * exchanges), ranged attackers fire discrete shots resolved by a seeded
 * roll, and the dead are removed with their consequences (spilled units,
 * plunder, raze payouts, last stand). Also records attacker reveals and
 * exposes `shelteredUnits`/`engagements` for the UI.
 */
import { axialHexDistance } from '../../hexmap/coords';
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { axialNeighbors, hexesWithinRadius } from '../../hexmap/model/map';
import type { HexTileMapKey } from '../../hexmap/model/types';
import type { AccuracyDef, RuleSet, ShotDef, ShotKind } from '../../rules/schema';
import {
  buildingRange,
  buildingStrength,
  type CityBonus,
  cityBonuses,
  clampHp,
  syncLastStand,
} from '../bonuses';
import { tickDerived } from '../derived';
import { invalidateDerived } from '../derivedRegistry';
import { toMilli } from '../fixed';
import { buildingCells, distanceTo } from '../footprint';
import { buildingInvestment, buildingLevel, levelDef } from '../levels';
import { cityStores, deployAll, deployUnit, storageCapacity, unitsByMint } from '../occupancy';
import { isHidden } from '../spies';
import { deposit, holdsStock, plunderDepositCity } from '../stock';
import { territoryOwners } from '../territory';
import type {
  Building,
  Cell,
  EntityId,
  PlayerId,
  Shot,
  SimContext,
  SimState,
  SystemFn,
  Unit,
} from '../types';
import { gainXp, unitStrength } from '../veterancy';
import { canHeal } from './heal';
import { attackTargetOf, canHit } from './pursuit';

const ATTACK_ACTION = 'attack';

function entityIdSuffix(id: string): number {
  return Number.parseInt(id.slice(1), 10);
}

/**
 * What `attackTarget` ranks a candidate by, worked out once per tick rather
 * than per attacker.
 */
interface Rank {
  /** Id suffix, the final tie-break. */
  suffix: number;
  /** Whether it has an attack that may hit a unit (what a unit attacker ranks first). */
  hurtsUnit: boolean;
  /** A healer or siege unit. */
  role: boolean;
}

type Candidate = ({ kind: 'unit'; entity: Unit } | { kind: 'building'; entity: Building }) & Rank;

interface Candidates {
  all: Candidate[];
  byId: Map<EntityId, Candidate>;
  /**
   * Tile key → candidates on it (a building on every tile of its footprint),
   * so attackers only look at tiles within their range.
   */
  byCell: Map<string, Candidate[]>;
}

/**
 * A unit that attacks (as `attackersOf`) and which kinds its rules `targets`
 * let it hit, without regard to range or where it stands.
 */
function unitRank(rules: RuleSet, u: Unit): Rank {
  const def = rules.units[u.type];
  const armed = !!def?.actions.includes(ATTACK_ACTION) && def.strength > 0;
  // Healers and siege (`targets: buildings`, or a `bonusVsBuildings` above 1)
  // matter more than their own hp suggests: they win fights or end cities.
  const role =
    canHeal(rules, u) || def?.targets === 'buildings' || (def?.bonusVsBuildings ?? 1) > 1;
  return {
    suffix: entityIdSuffix(u.id),
    hurtsUnit: armed && canHit(rules, u.type, true),
    role,
  };
}

function candidatesOf(
  state: SimState,
  rules: RuleSet,
  bonuses: ReadonlyMap<EntityId, CityBonus>
): Candidates {
  const all = [
    // Units inside a building, and hidden ones (unexposed spies), can't be hit.
    ...Object.values(state.units)
      .filter((u) => !u.inside && !isHidden(rules, u))
      .map((u): Candidate => ({ kind: 'unit', entity: u, ...unitRank(rules, u) })),
    ...Object.values(state.buildings).map((b): Candidate => {
      // A firing building (as `attackersOf`) hits units and buildings alike.
      const armed = b.buildProgress >= 1 && buildingStrength(rules, b, bonuses) > 0;
      return {
        kind: 'building',
        entity: b,
        suffix: entityIdSuffix(b.id),
        hurtsUnit: armed,
        role: false,
      };
    }),
  ];
  const byCell = new Map<string, Candidate[]>();
  const byId = new Map<EntityId, Candidate>();
  for (const cand of all) {
    byId.set(cand.entity.id, cand);
    const cells = cand.kind === 'building' ? buildingCells(cand.entity) : [cand.entity.pos];
    for (const cell of cells) {
      const key = getHexTileMapKey(cell);
      const list = byCell.get(key);
      if (list) list.push(cand);
      else byCell.set(key, [cand]);
    }
  }
  return { all, byId, byCell };
}

interface Attacker {
  entity: Unit | Building;
  strength: number;
  range: number;
  /** Target of the unit's attack order, hit first while in range. */
  preferred?: Unit | Building;
  /**
   * Enemy units that hit it in the last RETALIATE_TICKS (`Unit.hitBy`): with
   * a building as `preferred`, it fights those in range first.
   */
  hitBy?: readonly EntityId[];
  /** A passive unit (`Unit.stance`): hits only its ordered target, never retaliates or picks one. */
  passive?: true;
  /** Attacking unit's type, for its `targets` and `bonusVs` rules; undefined for buildings. */
  unitType?: string;
  /** A ranged attacker's shots (range 2+); undefined for melee (continuous damage). */
  shot?: Firing;
}

/** How a ranged attacker fires (rules `shot`, with rules.yaml `shots` defaults). */
interface Firing {
  kind: ShotKind;
  /** Seconds between shots. */
  fireInterval: number;
  accuracy: AccuracyDef;
}

/**
 * A unit type's or building's (at its level) `shot` rules, filled in from
 * rules.yaml `shots`: undefined unless it reaches 2 hexes or more, as melee
 * damage stays continuous.
 */
function firingOf(rules: RuleSet, def: ShotDef | undefined, range: number): Firing | undefined {
  if (range < 2) return undefined;
  return {
    kind: def?.kind ?? 'arrow',
    fireInterval: def?.fireInterval ?? rules.shots.fireInterval,
    accuracy: def?.accuracy ?? rules.shots.accuracy,
  };
}

/** A building's `shot` rules at its level (the highest level up to it that sets one, else its own). */
function buildingShotDef(rules: RuleSet, b: Building): ShotDef | undefined {
  const def = rules.buildings[b.type];
  if (!def) return undefined;
  for (let l = buildingLevel(b); l >= 2; l--) {
    const shot = levelDef(def, l)?.shot;
    if (shot) return shot;
  }
  return def.shot;
}

/**
 * How a unit or building fires, for the client and tests: undefined for one
 * that doesn't attack or fights in melee.
 */
function firingFor(rules: RuleSet, entity: Unit | Building): Firing | undefined {
  if ('buildProgress' in entity) {
    return firingOf(rules, buildingShotDef(rules, entity), buildingRange(rules, entity));
  }
  const def = rules.units[entity.type];
  // A healer's `range` is its healing reach, not a firing range.
  if (!def?.actions.includes('attack')) return undefined;
  return firingOf(rules, def.shot, def.range);
}

/** Chance a shot hits a target `distance` hexes away (see rules `accuracy`). */
function hitChance(accuracy: AccuracyDef, distance: number): number {
  const p = accuracy.base + accuracy.perHex * (Math.max(1, distance) - 1);
  return Math.min(1, Math.max(accuracy.min, p));
}

/** The fastest unit type's `speedMultiplier` (per rule set, cached). */
const maxSpeeds = new WeakMap<RuleSet, number>();

function maxSpeed(rules: RuleSet): number {
  let max = maxSpeeds.get(rules);
  if (max === undefined) {
    max = Math.max(0, ...Object.values(rules.units).map((u) => u.speedMultiplier));
    maxSpeeds.set(rules, max);
  }
  return max;
}

/**
 * Whether `unit` is on the move this tick: it has a path and isn't waiting
 * at a blocked step (movement runs before combat, so it just stepped).
 */
function isMoving(unit: Unit): boolean {
  return !!unit.path && unit.path.length > 0 && !unit.blocked;
}

/**
 * Factor on a shot's hit chance for its target: 1 for a building or a still
 * unit; for a moving unit 1 − `shots.movingPenalty[kind]` × its speed ÷ the
 * fastest unit type's (rules.yaml `shots`), so fast movers are the hardest
 * to hit and guided missiles and beams barely care.
 */
function movingFactor(rules: RuleSet, kind: ShotKind, target: Unit | Building): number {
  if ('buildProgress' in target || !isMoving(target)) return 1;
  const speed = rules.units[target.type]?.speedMultiplier ?? 0;
  const max = maxSpeed(rules);
  if (max <= 0) return 1;
  const penalty = rules.shots.movingPenalty[kind] ?? 0;
  return Math.max(0, 1 - (penalty * Math.min(speed, max)) / max);
}

/** Ticks a shot of `kind` flies over `distance` hexes (rules.yaml `shots.flight`), at least 1. */
function flightTicks(rules: RuleSet, kind: ShotKind, distance: number): number {
  const { base, perHex } = rules.shots.flight[kind];
  return Math.max(1, Math.round(base + perHex * distance));
}

/** Ticks between shots for a fire interval (at least 1). */
function reloadTicks(fireInterval: number, dt: number): number {
  return Math.max(1, Math.round(fireInterval / dt));
}

/**
 * A deterministic fraction in [0, 1) from the parts (FNV-1a over their
 * text): the sim's only randomness, so every peer rolls the same.
 */
function roll(...parts: readonly (string | number)[]): number {
  const text = parts.join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // A final avalanche so neighbouring ticks don't roll alike.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 0x100000000;
}

/**
 * Everything that attacks: units with the attack action and strength, and
 * completed buildings with strength (e.g. cities firing on raiders; a
 * barracks in a city's territory adds to its strength).
 */
function attackersOf(
  state: SimState,
  ctx: SimContext,
  bonuses: ReadonlyMap<EntityId, CityBonus>
): Attacker[] {
  const result: Attacker[] = [];
  for (const u of Object.values(state.units)) {
    const def = ctx.rules.units[u.type];
    if (!def?.actions.includes(ATTACK_ACTION) || def.strength <= 0) continue;
    if (u.inside) continue; // Units inside a building don't fight.
    const preferred =
      u.order?.kind === 'attack' ? attackTargetOf(state, ctx.rules, u.order.targetId) : undefined;
    const range = def.range;
    const passive = u.stance === 'passive';
    const hitBy =
      !passive && preferred && 'buildProgress' in preferred && u.hitBy
        ? Object.keys(u.hitBy).filter((id) => state.tick - (u.hitBy?.[id] ?? 0) <= RETALIATE_TICKS)
        : undefined;
    result.push({
      entity: u,
      strength: unitStrength(ctx.rules, u),
      range,
      preferred,
      hitBy,
      ...(passive ? { passive } : {}),
      unitType: u.type,
      shot: firingOf(ctx.rules, def.shot, range),
    });
  }
  for (const b of Object.values(state.buildings)) {
    if (b.buildProgress < 1) continue;
    const strength = buildingStrength(ctx.rules, b, bonuses);
    if (strength <= 0) continue;
    const range = buildingRange(ctx.rules, b);
    result.push({
      entity: b,
      strength,
      range,
      shot: firingOf(ctx.rules, buildingShotDef(ctx.rules, b), range),
    });
  }
  return result;
}

/**
 * Units standing anywhere in their own completed `garrison` city's territory
 * (`territoryOwners` only holds completed cities). Enemy units there aren't
 * protected, and neither are healers: they never attack, so shelter would
 * make them untouchable at home, and they're war units like any fighter.
 */
function garrisonedUnits(
  state: SimState,
  ctx: SimContext,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): Set<EntityId> {
  const result = new Set<EntityId>();
  if (owners.size === 0) return result;
  for (const u of Object.values(state.units)) {
    if (canHeal(ctx.rules, u)) continue;
    const city = state.buildings[owners.get(getHexTileMapKey(u.pos)) ?? ''];
    if (city?.owner === u.owner && ctx.rules.buildings[city.type]?.garrison) result.add(u.id);
  }
  return result;
}

/**
 * Whether `a` is a better default pick than `b` for a unit attacker, in
 * order: it can hurt a unit, then lower current hp, then healer or siege,
 * then lower id suffix. Suffixes are unique, so the result doesn't depend on
 * scan order.
 */
function outranks(a: Candidate, b: Candidate): boolean {
  if (a.hurtsUnit !== b.hurtsUnit) return a.hurtsUnit;
  if (a.entity.hp !== b.entity.hp) return a.entity.hp < b.entity.hp;
  if (a.role !== b.role) return a.role;
  return a.suffix < b.suffix;
}

/**
 * The enemy `attacker` hits this tick: a unit that hit it lately (`hitBy`,
 * lowest id suffix first) while it's in range and not protected, so a unit
 * sent at a building fights back against its defenders; then its ordered
 * target when that's in range and not protected, else the best enemy unit or
 * building within its range (skipping `protectedIds`). A unit attacker
 * ranks by `outranks`: enemies that can hurt a unit first (armed, with rules
 * `targets` that let them hit units; judged from rules and state alone, so
 * they needn't be in range of the attacker), then lowest current hp, then
 * healers and siege, then lowest id suffix. A building (a city) keeps the
 * plain lowest id suffix (suffixes are unique across units and buildings, so
 * scan order doesn't matter). Undefined if nothing is in range, and for a
 * passive unit whose ordered target isn't.
 */
function attackTarget(
  rules: SimContext['rules'],
  attacker: Attacker,
  candidates: Candidates,
  protectedIds: ReadonlySet<EntityId>
): EntityId | undefined {
  // A unit's rules `targets` limit what it may hit (a ram: buildings only).
  const hittable = (cand: Candidate | undefined) =>
    !!cand &&
    (attacker.unitType === undefined || canHit(rules, attacker.unitType, cand.kind === 'unit'));
  let retaliate: EntityId | undefined;
  let retaliateSuffix = Number.POSITIVE_INFINITY;
  for (const id of attacker.hitBy ?? []) {
    const cand = candidates.byId.get(id);
    if (cand?.kind !== 'unit' || !hittable(cand)) continue;
    if (cand.entity.owner === attacker.entity.owner || protectedIds.has(id)) continue;
    if (axialHexDistance(cand.entity.pos, attacker.entity.pos) > attacker.range) continue;
    const suf = entityIdSuffix(id);
    if (suf < retaliateSuffix) {
      retaliateSuffix = suf;
      retaliate = id;
    }
  }
  if (retaliate !== undefined) return retaliate;
  const preferred = attacker.preferred;
  if (
    preferred &&
    hittable(candidates.byId.get(preferred.id)) &&
    preferred.owner !== attacker.entity.owner &&
    !protectedIds.has(preferred.id) &&
    distanceTo(preferred, attacker.entity.pos) <= attacker.range
  ) {
    return preferred.id;
  }
  if (attacker.passive) return undefined;
  const ranked = attacker.unitType !== undefined;
  let best: Candidate | undefined;
  for (const cell of hexesWithinRadius(attacker.entity.pos, attacker.range)) {
    for (const cand of candidates.byCell.get(getHexTileMapKey(cell)) ?? []) {
      if (cand.entity.owner === attacker.entity.owner) continue;
      if (protectedIds.has(cand.entity.id) || !hittable(cand)) continue;
      if (!best || (ranked ? outranks(cand, best) : cand.suffix < best.suffix)) best = cand;
    }
  }
  return best?.entity.id;
}

/**
 * Damage multiplier of an attack: the attacking unit type's `bonusVs` against
 * a unit target (`bonusVsBuildings` against a building), times
 * (1 − `pacing.homeDefence`) for a unit target standing in its owner's
 * territory.
 */
function damageBonus(
  ctx: SimContext,
  attacker: Attacker,
  target: Candidate | undefined,
  owners: ReadonlyMap<HexTileMapKey, EntityId>,
  state: SimState
): number {
  if (!target) return 1;
  if (target.kind !== 'unit') {
    if (attacker.unitType === undefined) return 1;
    return ctx.rules.units[attacker.unitType]?.bonusVsBuildings ?? 1;
  }
  const bonus =
    attacker.unitType === undefined
      ? 1
      : (ctx.rules.units[attacker.unitType]?.bonusVs?.[target.entity.type] ?? 1);
  const home = state.buildings[owners.get(getHexTileMapKey(target.entity.pos)) ?? ''];
  return home?.owner === target.entity.owner ? bonus * (1 - ctx.rules.pacing.homeDefence) : bonus;
}

interface Attack {
  attacker: Attacker;
  targetId: EntityId;
}

/**
 * Every attack happening this tick. Garrisoned units are protected unless
 * they attack: first work out which garrisoned units have a target that
 * isn't itself garrisoned, then target with only the rest protected.
 */
function attacksOf(
  state: SimState,
  ctx: SimContext
): {
  attacks: Attack[];
  sheltered: Set<EntityId>;
  candidates: Candidates;
  owners: ReadonlyMap<HexTileMapKey, EntityId>;
} {
  // Territory once per call: shared by barracks bonuses and garrison shelter.
  const owners = territoryOwners(state, ctx.rules);
  const bonuses = cityBonuses(state, ctx.rules, owners);
  const candidates = candidatesOf(state, ctx.rules, bonuses);
  const attackers = attackersOf(state, ctx, bonuses);
  const garrisoned = garrisonedUnits(state, ctx, owners);
  const sheltered = new Set(garrisoned);
  for (const a of attackers) {
    if (!garrisoned.has(a.entity.id)) continue;
    if (attackTarget(ctx.rules, a, candidates, garrisoned) !== undefined)
      sheltered.delete(a.entity.id);
  }
  const attacks: Attack[] = [];
  for (const attacker of attackers) {
    const targetId = attackTarget(ctx.rules, attacker, candidates, sheltered);
    if (targetId !== undefined) attacks.push({ attacker, targetId });
  }
  return { attacks, sheltered, candidates, owners };
}

/**
 * `engagements` and `shelteredUnits` from one targeting pass, for a caller
 * that wants both.
 */
function combatView(
  state: SimState,
  ctx: SimContext
): { engagements: Map<EntityId, EntityId>; sheltered: Set<EntityId> } {
  const { attacks, sheltered } = attacksOf(state, ctx);
  const engaged = new Map<EntityId, EntityId>();
  for (const { attacker, targetId } of attacks) engaged.set(attacker.entity.id, targetId);
  return { engagements: engaged, sheltered };
}

/** Attacker id (unit or building) → target id for every attack happening this tick. */
function engagements(state: SimState, ctx: SimContext): Map<EntityId, EntityId> {
  return combatView(state, ctx).engagements;
}

/** Units protected by their city's territory this tick (garrisoned and not attacking). */
function shelteredUnits(state: SimState, ctx: SimContext): Set<EntityId> {
  return attacksOf(state, ctx).sheltered;
}

/**
 * The player who did the most damage to a target this tick (ties to the lower
 * player id).
 */
function topAttacker(damage: Record<PlayerId, number>): PlayerId | undefined {
  let best: PlayerId | undefined;
  for (const id of Object.keys(damage).sort()) {
    if (best === undefined || damage[id] > damage[best]) best = id;
  }
  return best;
}

/**
 * A destroyed city's stock: `pacing.plunderShare` of each resource goes to the
 * attacker's city holding the most of it (see `plunderDepositCity`); the rest,
 * and everything when they have no completed city, is lost.
 */
function plunder(
  state: SimState,
  ctx: SimContext,
  city: Building,
  attacker: PlayerId | undefined
): void {
  const share = ctx.rules.pacing.plunderShare;
  const player = attacker === undefined ? undefined : state.players[attacker];
  if (!city.stock || attacker === undefined || !player || share <= 0) return;
  const amounts: Record<string, number> = {};
  for (const res of Object.keys(city.stock).sort()) {
    const amount = Math.round((city.stock[res] ?? 0) * share);
    const to = plunderDepositCity(state, ctx.rules, attacker, res, city.pos);
    if (!to || amount <= 0) continue;
    deposit(state, to, res, amount);
    amounts[res] = amount;
  }
  if (Object.keys(amounts).length === 0) return;
  // Several cities falling to one player in a tick report as one plunder
  // (summed amounts, located at the last city) since the client diffs by tick.
  const prev = player.lastPlunder;
  if (prev?.tick === state.tick) {
    for (const [res, amount] of Object.entries(prev.amounts)) {
      amounts[res] = (amounts[res] ?? 0) + amount;
    }
  }
  player.lastPlunder = { tick: state.tick, from: city.owner, pos: { ...city.pos }, amounts };
}

/**
 * A destroyed rival building: `pacing.razeShare` of what was invested in it
 * (cost plus levels, and an upgrade in progress at what was paid in full, not
 * by its progress: simpler, and the owner lost all of it; see
 * `buildingInvestment`) goes to `attacker`, per
 * resource into their city holding the most of it (see `plunderDepositCity`).
 * Nothing when they have no completed city.
 */
function raze(
  state: SimState,
  ctx: SimContext,
  building: Building,
  attacker: PlayerId | undefined
): void {
  const share = ctx.rules.pacing.razeShare;
  const player = attacker === undefined ? undefined : state.players[attacker];
  if (attacker === undefined || !player || attacker === building.owner || share <= 0) return;
  const invested = buildingInvestment(ctx.rules, building);
  const amounts: Record<string, number> = {};
  for (const res of Object.keys(invested).sort()) {
    const amount = toMilli((invested[res] ?? 0) * share);
    const to = plunderDepositCity(state, ctx.rules, attacker, res, building.pos);
    if (!to || amount <= 0) continue;
    deposit(state, to, res, amount);
    amounts[res] = amount;
  }
  if (Object.keys(amounts).length === 0) return;
  // Several buildings razed by one player in a tick report as one (summed,
  // at the last one) since the client diffs by tick.
  const prev = player.lastRaze;
  let count = 1;
  if (prev?.tick === state.tick) {
    count += prev.count;
    for (const [res, amount] of Object.entries(prev.amounts)) {
      amounts[res] = (amounts[res] ?? 0) + amount;
    }
  }
  player.lastRaze = {
    tick: state.tick,
    from: building.owner,
    pos: { ...building.pos },
    type: building.type,
    amounts,
    count,
  };
}

/**
 * Buildings under attack: a damaged city or barracks deploys the units
 * stored in that city's barracks to defend; a destroyed building spills the
 * units inside it onto the nearest free tiles within DEPLOY_RADIUS of any
 * tile it covered (those tiles included), and one with nowhere to go dies.
 */
function alarm(
  state: SimState,
  ctx: SimContext,
  hit: readonly Building[],
  fallen: ReadonlyMap<EntityId, Cell[] | undefined>
): void {
  const derived = tickDerived(state, ctx);
  const occ = derived.occupancy();
  const owners = derived.owners();
  const alerted = new Set<EntityId>();
  for (const b of hit) {
    if (storageCapacity(ctx.rules, b) > 0) alerted.add(b.id);
    if (!b.stock && ctx.rules.buildings[b.type]?.territoryRadius === undefined) continue;
    for (const store of cityStores(state, ctx.rules, b.id, owners)) alerted.add(store.id);
  }
  for (const id of [...alerted].sort((a, c) => entityIdSuffix(a) - entityIdSuffix(c))) {
    deployAll(state, ctx, occ, id);
  }
  for (const u of unitsByMint(state)) {
    if (!u.inside || !fallen.has(u.inside)) continue;
    if (!deployUnit(state, ctx, occ, u, 0, fallen.get(u.inside))) delete state.units[u.id];
  }
}

/** The tile of `target` a shot from `from` aims at: its nearest one. */
function aimCell(target: Candidate, from: Cell): Cell {
  if (target.kind === 'unit') return target.entity.pos;
  let best = target.entity.pos;
  let bestDistance = axialHexDistance(best, from);
  for (const cell of buildingCells(target.entity)) {
    const d = axialHexDistance(cell, from);
    if (d < bestDistance) {
      best = cell;
      bestDistance = d;
    }
  }
  return best;
}

/**
 * `attacker` fires one shot at `target`: the hit is rolled now (from the
 * tick and both ids, so every peer agrees) with its `accuracy` at the
 * distance, less for a moving unit (`movingFactor`), and a hit's damage (strength × fire interval × `bonus`, so
 * strength stays the dps at 100% accuracy) lands `flightTicks` later. A miss
 * lands on a tile next to the target.
 */
function fire(
  state: SimState,
  ctx: SimContext,
  attacker: Attacker,
  firing: Firing,
  target: Candidate,
  bonus: number,
  dt: number
): Shot {
  const from = attacker.entity.pos;
  const aim = aimCell(target, from);
  const distance = distanceTo(target.entity, from);
  const chance =
    hitChance(firing.accuracy, distance) * movingFactor(ctx.rules, firing.kind, target.entity);
  const hit = roll(state.tick, attacker.entity.id, target.entity.id) < chance;
  const ticks = reloadTicks(firing.fireInterval, dt);
  const neighbours = axialNeighbors(aim);
  const stray =
    neighbours[Math.floor(roll('miss', state.tick, attacker.entity.id) * neighbours.length)] ?? aim;
  return {
    id: `${attacker.entity.id}@${state.tick}`,
    attacker: attacker.entity.id,
    owner: attacker.entity.owner,
    kind: firing.kind,
    targetId: target.entity.id,
    from: { ...from },
    to: { ...(hit ? aim : stray) },
    fireTick: state.tick,
    impactTick: state.tick + flightTicks(ctx.rules, firing.kind, distance),
    hit,
    damage: hit ? toMilli(attacker.strength * ticks * dt * bonus) : 0,
  };
}

/** Counts every ranged attacker's reload down by a tick (missing once ready). */
function reload(state: SimState): void {
  for (const e of [...Object.values(state.units), ...Object.values(state.buildings)]) {
    if (e.reload === undefined) continue;
    if (e.reload > 1) e.reload -= 1;
    else delete e.reload;
  }
}

/** Ticks (2 s) a unit on an attack order remembers an enemy unit that hit it (`Unit.hitBy`). */
const RETALIATE_TICKS = 20;
/** Most attackers one unit remembers; bounds snapshots. */
const MAX_HIT_BY = 16;

/**
 * Records, on each unit with an attack order that takes damage this tick,
 * the enemy units dealing it (`Unit.hitBy`), and forgets hits older than
 * RETALIATE_TICKS or from attackers that are gone. Units without an attack
 * order keep no record: only one sent at a building looks at it.
 */
function recordHits(state: SimState, blows: readonly Blow[]): void {
  for (const u of Object.values(state.units)) {
    const hitBy = u.hitBy;
    if (!hitBy) continue;
    for (const [id, at] of Object.entries(hitBy)) {
      if (state.tick - at > RETALIATE_TICKS || !state.units[id]) delete hitBy[id];
    }
    if (u.order?.kind !== 'attack' || Object.keys(hitBy).length === 0) delete u.hitBy;
  }
  for (const { attackerId, owner, targetId, damage } of blows) {
    if (damage <= 0 || !state.units[attackerId]) continue;
    const victim = state.units[targetId];
    if (!victim || victim.owner === owner || victim.order?.kind !== 'attack') continue;
    const hitBy = victim.hitBy ?? {};
    if (hitBy[attackerId] === undefined && Object.keys(hitBy).length >= MAX_HIT_BY) continue;
    hitBy[attackerId] = state.tick;
    victim.hitBy = hitBy;
  }
}

/** Ticks an attacker stays revealed to the player it damaged (5 s). */
const REVEAL_TICKS = 50;
/** Most attackers one player keeps revealed; bounds snapshots and vision work. */
const MAX_REVEALED = 1000;

/** Drops reveals that have run out or whose attacker is gone. */
function pruneReveals(state: SimState): void {
  for (const player of Object.values(state.players)) {
    const revealed = player.revealed;
    if (!revealed) continue;
    for (const [id, until] of Object.entries(revealed)) {
      if (until <= state.tick || (!state.units[id] && !state.buildings[id])) delete revealed[id];
    }
    if (Object.keys(revealed).length === 0) delete player.revealed;
  }
}

/**
 * Every unit or building whose damage lands on a rival's unit or building
 * this tick (a melee hit or a shot that hits) is revealed to that rival for
 * `REVEAL_TICKS`, even through fog (see vision.ts), so whoever attacks you
 * can be seen and hit back.
 */
function revealAttackers(state: SimState, candidates: Candidates, blows: readonly Blow[]): void {
  for (const { attackerId, owner, targetId, damage } of blows) {
    if (damage <= 0) continue;
    if (!state.units[attackerId] && !state.buildings[attackerId]) continue;
    const victim = candidates.byId.get(targetId)?.entity.owner;
    const player = victim === undefined ? undefined : state.players[victim];
    if (!player || victim === owner) continue;
    const revealed = player.revealed ?? {};
    if (revealed[attackerId] === undefined && Object.keys(revealed).length >= MAX_REVEALED)
      continue;
    revealed[attackerId] = state.tick + REVEAL_TICKS;
    player.revealed = revealed;
  }
}

/** Damage landing this tick from `attacker` (a unit or building id, maybe gone) of `owner`. */
interface Blow {
  attackerId: EntityId;
  owner: PlayerId;
  targetId: EntityId;
  damage: number;
}

/**
 * Combat each tick: melee attackers (range 1) deal their strength × dt
 * continuously; ranged ones (range 2+) fire a shot whenever reloaded (see
 * `fire`), and shots reaching their impact tick deal their damage if they
 * hit and the target is still there to be hit (not gone, not inside a
 * building). All damage landing in a tick is applied at once.
 */
const combat: SystemFn = (state: SimState, ctx, _inputs, dt: number): void => {
  // A city founded or lost since last tick starts or ends a last stand.
  syncLastStand(state, ctx.rules);
  const linger = ctx.rules.shots.linger;
  if (state.shots) {
    state.shots = state.shots.filter((s) => s.impactTick + linger >= state.tick);
    if (state.shots.length === 0) delete state.shots;
  }
  reload(state);
  pruneReveals(state);
  const { attacks, candidates, owners } = attacksOf(state, ctx);
  const tally: Record<string, number> = {};
  // Target id → attacking player → damage, to credit plunder and razing.
  const dealt: Record<EntityId, Record<PlayerId, number>> = {};

  const blows: Blow[] = [];
  const fired: Shot[] = [];
  for (const { attacker, targetId } of attacks) {
    const target = candidates.byId.get(targetId);
    if (!target) continue;
    const bonus = damageBonus(ctx, attacker, target, owners, state);
    if (!attacker.shot) {
      const damage = toMilli(attacker.strength * dt * bonus);
      blows.push({
        attackerId: attacker.entity.id,
        owner: attacker.entity.owner,
        targetId,
        damage,
      });
      continue;
    }
    if (attacker.entity.reload !== undefined) continue;
    fired.push(fire(state, ctx, attacker, attacker.shot, target, bonus, dt));
    attacker.entity.reload = reloadTicks(attacker.shot.fireInterval, dt);
  }
  // Shots fired earlier landing now (never this tick's: they fly at least a tick).
  for (const shot of state.shots ?? []) {
    if (shot.impactTick !== state.tick || !shot.hit || !candidates.byId.has(shot.targetId))
      continue;
    blows.push({
      attackerId: shot.attacker,
      owner: shot.owner,
      targetId: shot.targetId,
      damage: shot.damage,
    });
  }
  if (fired.length > 0) state.shots = [...(state.shots ?? []), ...fired];

  // Pass 1: damage into tally (simultaneous)
  for (const { owner, targetId, damage } of blows) {
    tally[targetId] = (tally[targetId] ?? 0) + damage;
    const byOwner = dealt[targetId] ?? {};
    byOwner[owner] = (byOwner[owner] ?? 0) + damage;
    dealt[targetId] = byOwner;
  }

  // Pass 2: apply damage or remove entities
  let buildingLost = false;
  const fallen: Building[] = [];
  const fallenCities: Building[] = [];
  const hit: Building[] = [];
  // Fallen buildings with units inside → the extra tiles they covered, to spill them around.
  const fallenHomes = new Map<EntityId, Cell[] | undefined>();
  const occupied = new Set<EntityId>();
  for (const u of Object.values(state.units)) if (u.inside) occupied.add(u.inside);
  for (const cand of candidates.all) {
    const id = cand.entity.id;
    const dmg = tally[id];
    if (dmg === undefined || dmg === 0) continue;

    if (cand.entity.hp - dmg <= 0) {
      if (cand.kind === 'unit') {
        delete state.units[id];
      } else {
        delete state.buildings[id];
        buildingLost = true;
        fallen.push(cand.entity);
        if (cand.entity.stock) fallenCities.push(cand.entity);
        const loser = state.players[cand.entity.owner];
        if (loser && holdsStock(ctx.rules, cand.entity)) loser.lostCity = true;
        if (occupied.has(id)) fallenHomes.set(id, cand.entity.footprint);
      }
    } else {
      cand.entity.hp -= dmg;
      if (cand.kind === 'building') {
        hit.push(cand.entity);
        cand.entity.lastDamagedTick = state.tick;
      }
    }
  }
  // Units and buildings died: what the tick worked out from them is stale.
  invalidateDerived(state);
  // Xp for damage dealt (a shot's once it lands), to units that survived the exchange.
  for (const { attackerId, targetId, damage } of blows) {
    const unit = state.units[attackerId];
    if (!unit) continue;
    gainXp(ctx.rules, unit, damage, candidates.byId.get(targetId)?.kind === 'building');
  }
  // After removals: an attacker killed in the exchange isn't revealed.
  revealAttackers(state, candidates, blows);
  recordHits(state, blows);
  if (hit.length > 0 || fallenHomes.size > 0) alarm(state, ctx, hit, fallenHomes);
  // After every removal, so rewards only land in cities still standing.
  for (const b of fallen) raze(state, ctx, b, topAttacker(dealt[b.id] ?? {}));
  for (const city of fallenCities) plunder(state, ctx, city, topAttacker(dealt[city.id] ?? {}));
  // A fallen barracks takes its bonus hp with it; a fallen city can leave
  // its owner's last one on a last stand.
  if (buildingLost) {
    clampHp(state, ctx.rules);
    syncLastStand(state, ctx.rules);
  }
};

export type { Firing };
export {
  combat,
  combatView,
  engagements,
  firingFor,
  flightTicks,
  hitChance,
  MAX_HIT_BY,
  MAX_REVEALED,
  movingFactor,
  RETALIATE_TICKS,
  REVEAL_TICKS,
  roll,
  shelteredUnits,
};
